/**
 * NotificationBanner — in-app foreground push notification banner.
 *
 * Rendered in the full-width top-bar slot when a turn event targets a game
 * other than the one the user is currently viewing. It fills the active
 * screen's top chrome, using the same full-box treatment as reconnecting.
 *
 * Design:
 *   - Slides in from the top via a simple Animated.Value translate.
 *   - Auto-dismisses after AUTO_DISMISS_MS (4 500 ms).
 *   - Tapping the banner navigates to the target game and dismisses.
 *   - The "×" close chip dismisses without navigating.
 *
 * UX rationale for showing a custom in-app banner instead of relying on the
 * system notification:
 *   - We suppress the system banner in the Notifications.setNotificationHandler
 *     callback so we never show two banners at once.
 *   - The custom banner can match the WarCarp visual style (paper / olive /
 *     stencil font) and include an actionable "View game →" chevron CTA that
 *     the vanilla system banner does not support on Android.
 */

import React, { useEffect, useRef } from 'react'
import { Animated, Platform, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { usePathname } from 'expo-router'
import { notificationTopBarPlacement } from '../../lib/notificationBanner'
import { ThemeScope } from './ThemeScope'

const AUTO_DISMISS_MS = 4_500
const SLIDE_DURATION_MS = 220

export interface NotificationBannerProps {
  /** Notification title (maps to the push `title` field). */
  title: string
  /** Notification body / subtitle text. */
  body: string
  /** Called when the user taps the banner body (navigate CTA). */
  onPress: () => void
  /** Called when the user dismisses the banner (either via auto-dismiss or the × chip). */
  onDismiss: () => void
}

export function NotificationBanner({ title, body, onPress, onDismiss }: NotificationBannerProps) {
  const insets = useSafeAreaInsets()
  const pathname = usePathname()
  const { fontScale } = useWindowDimensions()
  // Translate from fully above the top bar (–100) to 0 (visible).
  const translateY = useRef(new Animated.Value(-100)).current
  const dismissedRef = useRef(false)

  function dismiss() {
    if (dismissedRef.current) return
    dismissedRef.current = true
    Animated.timing(translateY, {
      toValue: -120,
      duration: SLIDE_DURATION_MS,
      useNativeDriver: Platform.OS !== 'web',
    }).start(() => onDismiss())
  }

  useEffect(() => {
    // Slide in
    Animated.timing(translateY, {
      toValue: 0,
      duration: SLIDE_DURATION_MS,
      useNativeDriver: Platform.OS !== 'web',
    }).start()

    // Auto-dismiss
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handlePress() {
    dismiss()
    onPress()
  }

  const placement = notificationTopBarPlacement({ pathname, insetTop: insets.top, fontScale })

  // ThemeScope: the banner mounts beside the root ThemedShell, outside its
  // dark-palette scope, so on native it needs its own or it renders light.
  return (
    <ThemeScope
      style={{
        position: 'absolute',
        top: placement.top,
        left: placement.left,
        right: placement.right,
        height: placement.height,
        zIndex: 9999,
      }}
      // Prevent touch events from falling through to content beneath.
      pointerEvents="box-none"
    >
      <Animated.View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: placement.height,
          transform: [{ translateY }],
        }}
      >
        <NotificationBannerContent title={title} body={body} onPress={handlePress} onDismiss={dismiss} height={placement.height} />
      </Animated.View>
    </ThemeScope>
  )
}

/** Shared static content lets visual checkpoints exercise the actual banner typography. */
export function NotificationBannerContent({ title, body, onPress, onDismiss, height }: NotificationBannerProps & { height: number }) {
  return (
    <View
      className="bg-paper-up border-b border-olive px-4 flex-row items-center gap-3"
      style={{
        height,
        zIndex: 9999,
        elevation: 9999,
      }}
    >
      {/* The banner body and dismiss chip are sibling buttons on web.
          Keeping them out of one another avoids invalid nested <button>s. */}
      <TouchableOpacity
        activeOpacity={0.88}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`Notification: ${title}. ${body}. Tap to view game.`}
        className="flex-1 flex-row items-center gap-3"
      >
        {/* Text stack */}
        <View className="flex-1 gap-0.5">
          <Text className="text-ink font-stencil uppercase tracking-widest text-[10px]" style={{ fontSize: 10, lineHeight: 12, includeFontPadding: false }} numberOfLines={1}>
            {title}
          </Text>
          <Text className="text-ink-soft font-typed text-xs" style={{ fontSize: 12, lineHeight: 16, includeFontPadding: false }} numberOfLines={1}>
            {body}
          </Text>
        </View>

        {/* CTA chevron */}
        {/* aria-hidden hides the decorative chevron from screen-readers */}
        <Text className="text-blood font-stencil text-base" aria-hidden>
          ›
        </Text>
      </TouchableOpacity>

      {/* Dismiss chip */}
      <TouchableOpacity
        onPress={onDismiss}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel="Dismiss notification"
        className="ml-1"
      >
        <Text className="text-ink-soft font-typed text-base leading-none">×</Text>
      </TouchableOpacity>
    </View>
  )
}
